/**
 * AiMessage — отрисовка одного сообщения чата (user / assistant).
 *
 *  - user: брендовый gradient-bubble справа, max-width 85%, attach-чипы сверху
 *  - assistant: прозрачный «flowing» текст слева, аватар-ЭКГ, markdown + GFM (tables)
 *  - download chip: «Скачать {filename} ({rows} строк)»
 *  - Streaming-cursor когда сообщение в процессе
 */

import { memo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { tokenStore } from '@/api/client';
import { Button, Icon } from '@/shared/ui';
import type { AiMessage as AiMessageT, AiApproval } from '@/state/aiStore';
import { approveAction } from '@/hooks/api/useAiChat';
import { PulseAvatar } from './PulseAvatar';

export interface AiMessageProps {
  message: AiMessageT;
  /** Базовый URL для скачивания (по умолчанию `/api/exports/`). */
  exportBase?: string;
}

export const AiMessage = memo(function AiMessage({
  message,
  exportBase = '/api/exports/',
}: AiMessageProps) {
  if (message.role === 'user') return <UserBubble message={message} />;
  return <AssistantBubble message={message} exportBase={exportBase} />;
});

// User

function UserBubble({ message }: { message: AiMessageT }) {
  return (
    <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
      <div style={{ maxWidth: '85%' }}>
        {message.attachments && message.attachments.length > 0 && (
          <div
            className="row wrap gap6"
            style={{ marginBottom: 8, justifyContent: 'flex-end' }}
          >
            {message.attachments.map((att) => (
              <FileChip key={att.name} name={att.name} size={att.size} />
            ))}
          </div>
        )}
        <div
          style={{
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
            color: '#fff',
            borderRadius: '16px 4px 16px 16px',
            padding: '10px 14px',
            boxShadow: 'var(--e-primary-sm)',
            wordBreak: 'break-word',
          }}
        >
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14 }}>
            {message.content}
          </div>
        </div>
      </div>
    </div>
  );
}

// Assistant

function AssistantBubble({
  message,
  exportBase,
}: {
  message: AiMessageT;
  exportBase: string;
}) {
  const showCursor = message.isStreaming && message.content.length > 0;

  return (
    <div className="msg" style={{ marginBottom: 16, alignItems: 'flex-start' }}>
      <span className="msg__av">
        <PulseAvatar size={32} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {message.content && (
          <div className="ai-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {message.content}
            </ReactMarkdown>
            {showCursor && <StreamingCursor />}
          </div>
        )}

        {message.stopped && <StoppedBadge steps={message.toolCalls.length} hasText={message.content.length > 0} />}

        {message.approvals.length > 0 && (
          <div className="col gap8" style={{ marginTop: 12 }}>
            {message.approvals.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
          </div>
        )}

        {message.downloads.length > 0 && (
          <div className="col gap8" style={{ marginTop: 12 }}>
            {message.downloads.map((d) => (
              <DownloadCard
                key={d.export_id}
                filename={d.filename}
                rows={d.rows}
                columns={d.columns}
                url={`${exportBase}${d.export_id}`}
              />
            ))}
          </div>
        )}

        {message.error && (
          <div
            className="row center gap8"
            style={{
              marginTop: 8,
              padding: '9px 12px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--danger)',
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              fontSize: 13,
            }}
          >
            <Icon name="Alert" style={{ width: 16, height: 16, flex: 'none' }} />
            <span>{message.error}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const markdownComponents: Components = {
  // По умолчанию ReactMarkdown оборачивает в <p> — стили в .ai-md выше.
};

function StoppedBadge({ steps, hasText }: { steps: number; hasText: boolean }) {
  const stepLabel = steps > 0 ? ` · ${steps} ${plural(steps, 'шаг', 'шага', 'шагов')}` : '';
  return (
    <div
      className="row center gap6"
      style={{
        marginTop: hasText ? 8 : 0,
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--muted-fg, var(--text))',
        opacity: 0.7,
      }}
    >
      <Icon name="Square" size={11} style={{ flex: 'none' }} />
      <span>
        Остановлено{hasText ? ' — наработка сохранена и уйдёт в контекст' : ' до начала ответа'}
        {stepLabel}
      </span>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function StreamingCursor() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 8,
        height: 14,
        marginLeft: 2,
        verticalAlign: 'text-bottom',
        background: 'var(--primary)',
        borderRadius: 2,
        animation: 'ai-cursor-blink 1s infinite',
      }}
    />
  );
}

// Helpers

function DownloadCard({
  filename,
  rows,
  columns,
  url,
}: {
  filename: string;
  rows: number;
  columns: number;
  url: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const handleDownload = async () => {
    setBusy(true);
    setErr(false);
    try {
      // Эндпоинт требует JWT — прямой <a href> токен не передаёт. Качаем
      // авторизованным fetch → blob → object URL.
      const res = await fetch(url, {
        headers: tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {},
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="att-file" style={{ background: 'var(--surface)' }}>
      <span
        aria-hidden
        className="att-file__ic"
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
          color: '#fff',
          border: 'none',
        }}
      >
        <Icon name="Download" size={18} />
      </span>
      <div className="att-file__meta">
        <span className="att-file__name">{filename}</span>
        <span
          className="att-file__size"
          style={err ? { color: 'var(--danger-fg)' } : undefined}
        >
          {err
            ? 'Не удалось скачать — попробуйте ещё раз'
            : `${rows.toLocaleString('ru-RU')} строк · ${columns} колонок · UTF-8 BOM`}
        </span>
      </div>
      <Button
        variant="primary"
        size="sm"
        icon="Download"
        onClick={() => void handleDownload()}
        disabled={busy}
      >
        {busy ? 'Качаю…' : 'Скачать'}
      </Button>
    </div>
  );
}

function ApprovalCard({ approval }: { approval: AiApproval }) {
  const [busy, setBusy] = useState(false);
  const pending = approval.status === 'pending';

  const decide = async (decision: 'allow' | 'deny') => {
    setBusy(true);
    await approveAction(approval.id, decision);
    setBusy(false);
  };

  const accent =
    approval.status === 'approved'
      ? 'var(--success)'
      : approval.status === 'denied'
        ? 'var(--danger)'
        : 'var(--warning)';

  return (
    <div
      className="col gap8"
      style={{
        padding: '12px 14px',
        borderRadius: 'var(--r-md)',
        border: `1px solid ${accent}`,
        background: 'var(--surface)',
      }}
    >
      <div className="row center gap8">
        <Icon name="Shield" style={{ width: 16, height: 16, flex: 'none', color: accent }} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          {pending
            ? 'Подтвердите действие по боевому аккаунту'
            : approval.status === 'approved'
              ? 'Действие подтверждено'
              : 'Действие отклонено'}
        </span>
      </div>
      <code
        className="mono"
        style={{
          fontSize: 12,
          background: 'var(--surface-alt)',
          padding: '8px 10px',
          borderRadius: 'var(--r-sm)',
          wordBreak: 'break-all',
          color: 'var(--text)',
        }}
      >
        {approval.action || approval.tool}
      </code>
      {pending && (
        <div className="row gap8">
          <Button variant="primary" size="sm" icon="Check" disabled={busy} onClick={() => void decide('allow')}>
            Применить
          </Button>
          <Button variant="outline" size="sm" icon="XCircle" disabled={busy} onClick={() => void decide('deny')}>
            Отклонить
          </Button>
        </div>
      )}
    </div>
  );
}

function FileChip({ name, size }: { name: string; size: number }) {
  return (
    <span
      className="chip"
      style={{
        background: 'var(--primary-100)',
        color: 'var(--primary-dark)',
        maxWidth: '100%',
      }}
    >
      <Icon name="Paperclip" size={13} />
      <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      <span style={{ opacity: 0.6 }}>· {Math.max(1, Math.round(size / 1024))}KB</span>
    </span>
  );
}
